import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectSupportDialog = ({ 
  open, 
  onOpenChange, 
  quoteData,
  configuratorType 
}) => {
  const [step, setStep] = useState(1); // 1 = initial question, 2 = form
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // End user details
  const [endUserName, setEndUserName] = useState("");
  const [endUserCompany, setEndUserCompany] = useState("");
  const [endUserEmail, setEndUserEmail] = useState("");
  const [endUserPhone, setEndUserPhone] = useState("");
  const [projectedInstallDate, setProjectedInstallDate] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const handleNo = () => {
    setStep(1);
    onOpenChange(false);
    // Reset form state when closing
    setEndUserName("");
    setEndUserCompany("");
    setEndUserEmail("");
    setEndUserPhone("");
    setProjectedInstallDate("");
    setAdditionalNotes("");
    setError("");
  };

  const handleYes = () => {
    setStep(2);
  };

  const handleSubmit = async () => {
    setError("");
    
    // Validation
    if (!endUserName || !endUserCompany || !projectedInstallDate) {
      setError("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/project-support/request`, {
        quote_data: quoteData,
        configurator_type: configuratorType,
        end_user_name: endUserName,
        end_user_company: endUserCompany,
        end_user_email: endUserEmail,
        end_user_phone: endUserPhone,
        projected_install_date: projectedInstallDate,
        additional_notes: additionalNotes
      }, getAuthHeaders());

      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
        setStep(1);
        setSuccess(false);
        // Reset form
        setEndUserName("");
        setEndUserCompany("");
        setEndUserEmail("");
        setEndUserPhone("");
        setProjectedInstallDate("");
        setAdditionalNotes("");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle>Project support and discount</DialogTitle>
              <DialogDescription>
                This quote qualifies for project support and potential project discount (£20,000+).
              </DialogDescription>
            </DialogHeader>

            <div className="py-6">
              <p className="text-slate-700 mb-4">
                Would you like to request project support and enquire about project discount for this configuration?
              </p>
              <p className="text-sm text-slate-600">
                Our team can provide additional assistance with large-scale installations, 
                technical support, project management, and discuss potential project discounts.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={handleNo}>
                No, Thanks
              </Button>
              <Button onClick={handleYes}>
                Yes, Request Support
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Project Support Request</DialogTitle>
              <DialogDescription>
                Please provide details about the end user and installation
              </DialogDescription>
            </DialogHeader>

            {success ? (
              <Alert className="bg-green-50 border-green-200">
                <AlertDescription className="text-green-800">
                  ✓ Project support request submitted successfully! We'll be in touch soon.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="endUserName">End User Name *</Label>
                      <Input
                        id="endUserName"
                        value={endUserName}
                        onChange={(e) => setEndUserName(e.target.value)}
                        placeholder="Contact name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="endUserCompany">End User Company *</Label>
                      <Input
                        id="endUserCompany"
                        value={endUserCompany}
                        onChange={(e) => setEndUserCompany(e.target.value)}
                        placeholder="Company name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="endUserEmail">End User Email</Label>
                      <Input
                        id="endUserEmail"
                        type="email"
                        value={endUserEmail}
                        onChange={(e) => setEndUserEmail(e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <Label htmlFor="endUserPhone">End User Phone</Label>
                      <Input
                        id="endUserPhone"
                        value={endUserPhone}
                        onChange={(e) => setEndUserPhone(e.target.value)}
                        placeholder="Phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="projectedInstallDate">Projected Install Date *</Label>
                    <Input
                      id="projectedInstallDate"
                      type="date"
                      value={projectedInstallDate}
                      onChange={(e) => setProjectedInstallDate(e.target.value)}
                    />
                  </div>

                  <div>
                    <Label htmlFor="additionalNotes">Additional Notes</Label>
                    <Textarea
                      id="additionalNotes"
                      value={additionalNotes}
                      onChange={(e) => setAdditionalNotes(e.target.value)}
                      placeholder="Any additional information about the project..."
                      rows={4}
                    />
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-2">Quote Summary:</p>
                    <p className="text-sm text-slate-600">Project: {quoteData?.project_name}</p>
                    <p className="text-sm text-slate-600">Configurator: {configuratorType}</p>
                    <p className="text-sm text-slate-600">Installer Price: £{quoteData?.installer_total?.toFixed(2) || quoteData?.installer_price?.toFixed(2) || 'N/A'}</p>
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <Button variant="outline" onClick={handleNo} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting}>
                    {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Submit Request
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProjectSupportDialog;
